﻿import { Link } from "react-router"
import type { Locale } from "../content/types"
import { getEntryByIdString, getPathByIdString } from "../content/registry"
import { services } from "../content/services"
import { cases } from "../content/cases"
import { getArticlesForLocale } from "../content/knowledge/index"
import { locations } from "../content/locations"
import { ui } from "../content/ui"

interface SectionProps {
  locale: Locale
  title: string
  children: React.ReactNode
}

function RelatedSection({ title, children }: SectionProps) {
  return (
    <section className="mt-16">
      <h2 className="text-2xl font-bold text-text-primary mb-6">{title}</h2>
      {children}
    </section>
  )
}

export function RelatedServices({ locale, ids }: { locale: Locale; ids: string[] }) {
  const items = ids.map((id) => services.find((s) => s.id === id)).filter((s): s is NonNullable<typeof s> => Boolean(s))
  if (items.length === 0) return null
  const prefix = locale === "nl" ? "/nl/diensten/" : "/en/services/"
  return (
    <RelatedSection locale={locale} title={ui.related.services[locale]}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((service) => (
          <Link
            key={service.id}
            to={`${prefix}${service.slug[locale]}/`}
            className="group bg-card-bg border border-border-subtle rounded-card p-6 hover:shadow-lg transition-shadow"
          >
            <h3 className="text-lg font-bold text-text-primary mb-2 group-hover:text-button-primary transition-colors">
              {service.title[locale].split("|")[0].trim()}
            </h3>
            <p className="text-text-secondary text-sm leading-relaxed">{service.shortDescription[locale]}</p>
          </Link>
        ))}
      </div>
    </RelatedSection>
  )
}

export function RelatedCases({ locale, ids }: { locale: Locale; ids: string[] }) {
  const items = ids.map((id) => cases.find((c) => c.id === id)).filter((c): c is NonNullable<typeof c> => Boolean(c))
  if (items.length === 0) return null
  const prefix = locale === "nl" ? "/nl/cases/" : "/en/case-studies/"
  return (
    <RelatedSection locale={locale} title={ui.related.cases[locale]}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((c) => (
          <Link
            key={c.id}
            to={`${prefix}${c.slug[locale]}/`}
            className="group bg-card-bg border border-border-subtle rounded-card p-6 hover:shadow-lg transition-shadow"
          >
            <h3 className="text-lg font-bold text-text-primary mb-2 group-hover:text-button-primary transition-colors">
              {c.clientName}
            </h3>
            <p className="text-text-secondary text-sm leading-relaxed">{c.summary[locale]}</p>
          </Link>
        ))}
      </div>
    </RelatedSection>
  )
}

export function RelatedArticles({ locale, ids, excludeId }: { locale: Locale; ids: string[]; excludeId?: string }) {
  const filtered = ids.filter((id) => id !== excludeId)
  const items = filtered
    .map((id) => getArticlesForLocale(locale).find((a) => a.id === `insight:${id}`))
    .filter((a): a is NonNullable<typeof a> => Boolean(a))
  if (items.length === 0) return null
  const prefix = locale === "nl" ? "/nl/kennis/" : "/en/insights/"
  return (
    <RelatedSection locale={locale} title={ui.related.insights[locale]}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((article) => (
          <Link
            key={article.id}
            to={`${prefix}${article.slug[locale]}/`}
            className="group bg-card-bg border border-border-subtle rounded-card p-6 hover:shadow-lg transition-shadow"
          >
            <h3 className="text-lg font-bold text-text-primary mb-2 group-hover:text-button-primary transition-colors">
              {article.title[locale]}
            </h3>
            <p className="text-text-secondary text-sm leading-relaxed line-clamp-3">{article.description[locale]}</p>
          </Link>
        ))}
      </div>
    </RelatedSection>
  )
}

export function RelatedLocations({ locale, ids }: { locale: Locale; ids: string[] }) {
  const items = ids
    .map((id) => locations.find((l) => l.id === id))
    .filter((l): l is NonNullable<typeof l> => Boolean(l))
  if (items.length === 0) return null
  return (
    <RelatedSection locale={locale} title={ui.related.locations[locale]}>
      <div className="flex flex-wrap gap-4">
        {items.map((location) => {
          const path = getPathByIdString(`location:${location.id}`, locale)
          if (!path) return null
          const entry = getEntryByIdString(`location:${location.id}`)
          return (
            <Link
              key={location.id}
              to={path}
              className="group inline-flex flex-col bg-card-bg border border-border-subtle rounded-card px-5 py-3 hover:shadow-lg transition-shadow"
            >
              <span className="font-semibold text-text-primary group-hover:text-button-primary transition-colors">
                {location.h1?.[locale] ?? location.cityOrRegion}
              </span>
              <span className="text-sm text-text-secondary">
                {entry?.description[locale].slice(0, 80)}…
              </span>
            </Link>
          )
        })}
      </div>
    </RelatedSection>
  )
}
